import { useState } from "react";
import {
  Zap, Terminal, CheckCircle2, ChevronRight, ChevronDown,
  Copy, Check, AlertTriangle, ArrowRight, ExternalLink,
  BookOpen, Plug, Menu, X, Circle, Sun, Moon
} from "lucide-react";

const cn = (...c) => c.filter(Boolean).join(" ");

function CopyButton({ text, light }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className={cn("p-1.5 rounded-md transition-colors", light ? "hover:bg-zinc-200 text-zinc-400 hover:text-zinc-700" : "hover:bg-white/10 text-zinc-500 hover:text-white")}
    >
      {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
    </button>
  );
}

function CodeBlock({ code, language = "bash", d }) {
  return (
    <div className={cn("rounded-xl border overflow-hidden my-4", d ? "bg-zinc-950 border-zinc-800" : "bg-zinc-900 border-zinc-700")}>
      <div className={cn("flex items-center justify-between px-4 py-2 border-b", d ? "border-zinc-800" : "border-zinc-700")}>
        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{language}</span>
        <CopyButton text={code} light={false} />
      </div>
      <pre className="px-5 py-4 text-sm font-mono text-zinc-200 overflow-x-auto whitespace-pre-wrap">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Step({ num, title, children, done, active, d }) {
  return (
    <div className="flex gap-5">
      <div className="flex flex-col items-center">
        <div className={cn(
          "w-9 h-9 rounded-full flex items-center justify-center text-sm font-black shrink-0 border-2 transition-all",
          done ? "bg-emerald-500 border-emerald-500 text-white" :
          active ? "bg-purple-500 border-purple-500 text-white shadow-lg shadow-purple-500/30" :
          d ? "bg-zinc-900 border-zinc-700 text-zinc-500" : "bg-zinc-100 border-zinc-300 text-zinc-400"
        )}>
          {done ? <CheckCircle2 size={16} /> : num}
        </div>
        <div className={cn("w-0.5 flex-1 mt-2", d ? "bg-zinc-800" : "bg-zinc-200")} />
      </div>
      <div className="pb-10 flex-1 min-w-0">
        <h3 className={cn("text-lg font-black mb-3", active ? "text-purple-400" : d ? "text-white" : "text-zinc-900")}>{title}</h3>
        <div className={cn("text-sm leading-relaxed", d ? "text-zinc-400" : "text-zinc-600")}>{children}</div>
      </div>
    </div>
  );
}

function DecisionCard({ question, yes, no, unknown, d }) {
  const [choice, setChoice] = useState(null);
  return (
    <div className={cn("rounded-2xl border overflow-hidden my-6", d ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200 shadow-sm")}>
      <div className={cn("px-6 py-5 border-b", d ? "border-zinc-800 bg-zinc-800/50" : "border-zinc-100 bg-zinc-50")}>
        <p className="font-black text-base">{question}</p>
      </div>
      <div className="p-6">
        <div className="flex flex-wrap gap-3 mb-4">
          {[{ label: "Yes", key: "yes", color: "emerald" }, { label: "No", key: "no", color: "red" }, { label: "Not sure", key: "unknown", color: "amber" }].map(opt => (
            <button key={opt.key} onClick={() => setChoice(choice === opt.key ? null : opt.key)}
              className={cn("px-5 py-2.5 rounded-xl text-sm font-bold border-2 transition-all",
                choice === opt.key
                  ? opt.color === "emerald" ? "bg-emerald-500 border-emerald-500 text-white"
                  : opt.color === "red" ? "bg-red-500 border-red-500 text-white"
                  : "bg-amber-500 border-amber-500 text-white"
                  : d ? "border-zinc-700 bg-zinc-800 hover:border-zinc-600" : "border-zinc-200 bg-zinc-50 hover:border-zinc-300"
              )}>
              {opt.label}
            </button>
          ))}
        </div>
        {choice === "yes" && yes && (
          <div className="mt-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-sm text-emerald-400 font-medium">{yes}</div>
        )}
        {choice === "no" && no && (
          <div className="mt-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400 font-medium">{no}</div>
        )}
        {choice === "unknown" && unknown && (
          <div className="mt-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-sm text-amber-400 font-medium">{unknown}</div>
        )}
      </div>
    </div>
  );
}

function Badge({ children, color = "purple" }) {
  const colors = {
    purple: "bg-purple-400/10 text-purple-400 border-purple-400/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  };
  return <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border", colors[color])}>{children}</span>;
}

function MCPCard({ name, icon, description, configPath, configContent, command, note, d }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("rounded-2xl border overflow-hidden transition-all", d ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200 shadow-sm")}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-5 text-left">
        <div className="flex items-center gap-4">
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black", d ? "bg-zinc-800" : "bg-zinc-100")}>
            {icon}
          </div>
          <div>
            <h4 className="font-black text-base">{name}</h4>
            <p className={cn("text-xs mt-0.5", d ? "text-zinc-500" : "text-zinc-400")}>{description}</p>
          </div>
        </div>
        {open ? <ChevronDown size={18} className="text-zinc-500 shrink-0" /> : <ChevronRight size={18} className="text-zinc-500 shrink-0" />}
      </button>
      {open && (
        <div className={cn("px-6 pb-6 border-t space-y-5", d ? "border-zinc-800" : "border-zinc-100")}>
          <div className="pt-5">
            <p className={cn("text-sm leading-relaxed mb-4", d ? "text-zinc-400" : "text-zinc-600")}>{note}</p>
            {command && (
              <>
                <p className={cn("text-xs font-bold uppercase tracking-widest mb-2", d ? "text-zinc-500" : "text-zinc-400")}>Step 1 — Start the Thrust MCP Server</p>
                <CodeBlock code={command} d={d} />
              </>
            )}
            {configPath && (
              <>
                <p className={cn("text-xs font-bold uppercase tracking-widest mb-2 mt-4", d ? "text-zinc-500" : "text-zinc-400")}>Step 2 — Add to config at <code className="text-purple-400">{configPath}</code></p>
                <CodeBlock code={configContent} language="json" d={d} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const MCP_CLIENTS = [
  {
    name: "Cursor",
    icon: "⌥",
    description: "AI-powered IDE",
    note: "Cursor reads MCP server configs from a JSON file in your project or global settings. Once added, Cursor will automatically connect to Thrust's MCP server and use your project context in every AI interaction.",
    command: "thrust",
    configPath: ".cursor/mcp.json",
    configContent: `{
  "mcpServers": {
    "thrust": {
      "command": "thrust",
      "args": ["mcp"],
      "env": {}
    }
  }
}`
  },
  {
    name: "Claude Code",
    icon: "◆",
    description: "Anthropic's agentic coding CLI",
    note: "Claude Code supports MCP servers natively via the 'claude mcp add' command. This registers Thrust as a persistent MCP server across all your Claude Code sessions — no manual config editing needed.",
    command: "thrust",
    configPath: null,
    configContent: `# Run this once to register Thrust permanently
claude mcp add thrust -- thrust mcp`
  },
  {
    name: "Windsurf",
    icon: "🌊",
    description: "Codeium's agentic IDE",
    note: "Windsurf supports MCP via its settings panel or a config file at ~/.codeium/windsurf/mcp_config.json. Once registered, Cascade (Windsurf's AI) will have access to your Thrust project state.",
    command: "thrust",
    configPath: "~/.codeium/windsurf/mcp_config.json",
    configContent: `{
  "mcpServers": {
    "thrust": {
      "command": "thrust",
      "args": ["mcp"]
    }
  }
}`
  },
  {
    name: "n8n",
    icon: "⚡",
    description: "Workflow automation platform",
    note: "n8n supports MCP via its MCP Client Tool node. First start the Thrust MCP server, then configure the n8n node to connect to it. This lets you build automations that read your project state, trigger actions on milestones, and pipe context into any workflow.",
    command: "thrust",
    configPath: null,
    configContent: `# In n8n, add an "MCP Client" node with:
# Transport: stdio
# Command: thrust
# Arguments: mcp`
  },
  {
    name: "VS Code (Copilot)",
    icon: "{}",
    description: "GitHub Copilot with MCP support",
    note: "VS Code supports MCP servers in its settings.json under the 'mcp' key. Once configured, GitHub Copilot Chat and agent mode will automatically query Thrust for your project context before making suggestions.",
    command: "thrust",
    configPath: ".vscode/mcp.json",
    configContent: `{
  "servers": {
    "thrust": {
      "type": "stdio",
      "command": "thrust",
      "args": ["mcp"]
    }
  }
}`
  },
  {
    name: "Any MCP-Compatible Client",
    icon: "🔌",
    description: "Generic stdio MCP integration",
    note: "Thrust's MCP server follows the standard Model Context Protocol spec over stdio transport. Any MCP-compatible client can connect to it using the pattern below. The server exposes your active project's PRD, current thrust, recent timeline, and milestone status as readable resources.",
    command: "thrust mcp",
    configPath: null,
    configContent: `# Generic stdio MCP configuration
{
  "command": "thrust",
  "args": ["mcp"],
  "transport": "stdio"
}`
  }
];

const NAV = [
  { id: "installation", label: "Agent Installation", icon: Terminal },
  { id: "mcp", label: "Connecting External Tools", icon: Plug },
];

export default function DocsPage() {
  const [page, setPage] = useState("installation");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [d, setD] = useState(() => {
    try { return localStorage.getItem("thrust-theme") !== "light"; } catch { return true; }
  });

  const toggleTheme = () => {
    setD(v => { try { localStorage.setItem("thrust-theme", !v ? "dark" : "light"); } catch {} return !v; });
  };

  return (
    <div className={cn("min-h-screen font-sans transition-colors duration-300", d ? "bg-zinc-950 text-zinc-50" : "bg-zinc-50 text-zinc-900")}>

      {/* TOP NAV */}
      <nav className={cn("fixed top-0 w-full z-50 backdrop-blur-md border-b h-14 flex items-center", d ? "bg-zinc-950/90 border-zinc-800" : "bg-white/90 border-zinc-200")}>
        <div className="flex items-center justify-between px-5 w-full">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className={cn("md:hidden p-1.5 rounded-lg", d ? "hover:bg-zinc-800" : "hover:bg-zinc-100")}>
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <a href="/" className="flex items-center gap-2 font-black text-lg tracking-tighter italic">
              <Zap className="text-purple-400 fill-current" size={18} /> THRUST
            </a>
            <ChevronRight size={14} className="text-zinc-600" />
            <span className={cn("text-sm font-bold", d ? "text-zinc-400" : "text-zinc-500")}>Docs</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className={cn("p-1.5 rounded-lg transition-colors", d ? "hover:bg-zinc-800 text-zinc-400" : "hover:bg-zinc-100 text-zinc-500")}>
              {d ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <a href="/" className={cn("text-sm font-bold px-3 py-1.5 rounded-lg transition-colors border", d ? "border-zinc-700 hover:bg-zinc-800 text-zinc-300" : "border-zinc-200 hover:bg-zinc-100 text-zinc-700")}>
              ← Back to Home
            </a>
          </div>
        </div>
      </nav>

      <div className="flex pt-14 min-h-screen">

        {/* SIDEBAR */}
        <aside className={cn(
          "fixed md:sticky top-14 h-[calc(100vh-3.5rem)] w-64 shrink-0 border-r overflow-y-auto transition-all duration-300 z-40",
          d ? "bg-zinc-950 border-zinc-800" : "bg-white border-zinc-200",
          sidebarOpen ? "left-0" : "-left-64 md:left-0"
        )}>
          <div className="p-5 space-y-1">
            <p className={cn("text-[10px] font-black uppercase tracking-widest px-3 py-2", d ? "text-zinc-600" : "text-zinc-400")}>Documentation</p>
            {NAV.map(item => (
              <button key={item.id} onClick={() => { setPage(item.id); setSidebarOpen(false); }}
                className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-left transition-all",
                  page === item.id
                    ? "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                    : d ? "text-zinc-400 hover:bg-zinc-900 hover:text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                )}>
                <item.icon size={16} className={page === item.id ? "text-purple-400" : ""} />
                {item.label}
              </button>
            ))}

            <div className={cn("mt-6 pt-6 border-t", d ? "border-zinc-800" : "border-zinc-200")}>
              <p className={cn("text-[10px] font-black uppercase tracking-widest px-3 py-2", d ? "text-zinc-600" : "text-zinc-400")}>On This Page</p>
              {page === "installation" && (
                <div className="space-y-1">
                  {["Prerequisites", "Install the CLI", "Run Thrust", "What's Next"].map(s => (
                    <a key={s} href={`#${s.toLowerCase().replace(/\s/g,"-").replace("'","")}`} className={cn("block px-3 py-1.5 text-xs font-medium rounded-lg transition-colors", d ? "text-zinc-500 hover:text-zinc-300" : "text-zinc-400 hover:text-zinc-700")}>{s}</a>
                  ))}
                </div>
              )}
              {page === "mcp" && (
                <div className="space-y-1">
                  {MCP_CLIENTS.map(c => (
                    <a key={c.name} href="#" className={cn("block px-3 py-1.5 text-xs font-medium rounded-lg transition-colors", d ? "text-zinc-500 hover:text-zinc-300" : "text-zinc-400 hover:text-zinc-700")}>{c.name}</a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="flex-1 min-w-0 px-6 md:px-12 py-12 max-w-3xl mx-auto w-full">

          {/* ─── INSTALLATION PAGE ─── */}
          {page === "installation" && (
            <div>
              <div className="mb-12">
                <Badge>Getting Started</Badge>
                <h1 className="text-4xl md:text-5xl font-black mt-4 mb-4 tracking-tight">Agent Installation</h1>
                <p className={cn("text-lg leading-relaxed", d ? "text-zinc-400" : "text-zinc-600")}>
                  The Thrust local agent is a lightweight CLI that watches your project folder, runs a local MCP server, and keeps your project context alive between sessions. Installation takes under two minutes.
                </p>
              </div>

              {/* STEP 1 — Node */}
              <div id="prerequisites">
                <Step num="1" title="Check your Node.js installation" active d={d}>
                  <p className="mb-4">The Thrust agent requires Node.js 18 or higher. Run the check below in your terminal to see if you have it installed.</p>

                  <DecisionCard
                    d={d}
                    question="Do you have Node.js installed?"
                    yes="You're good to go. Move on to Step 2."
                    unknown={<span>Run <code className="bg-black/20 px-1 rounded font-mono">node -v</code> in your terminal. If you see a version number like <code className="bg-black/20 px-1 rounded font-mono">v20.11.0</code>, you're ready. If you get an error, select <strong>No</strong> instead.</span>}
                    no={<span>Download and install Node.js from <strong>nodejs.org</strong>. Choose the LTS version. Once installed, restart your terminal and run <code className="bg-black/20 px-1 rounded font-mono">node -v</code> to confirm it's working.</span>}
                  />

                  <CodeBlock code="node -v" d={d} />

                  <div className={cn("flex gap-3 p-4 rounded-xl border text-sm mt-2", d ? "border-amber-500/20 bg-amber-500/5 text-amber-400" : "border-amber-300 bg-amber-50 text-amber-700")}>
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <span>You need Node.js v18 or newer. If your version is older, update it from nodejs.org before continuing.</span>
                  </div>
                </Step>
              </div>

              {/* STEP 2 — Install */}
              <div id="install-the-cli">
                <Step num="2" title="Install the Thrust CLI globally" d={d}>
                  <p className="mb-4">Install the agent globally so you can run it from any project folder on your machine.</p>
                  <CodeBlock code="npm install -g thrust-cli" d={d} />
                  <p className="mt-4 mb-4">Verify the install worked by checking the version:</p>
                  <CodeBlock code="thrust --version" d={d} />
                  <div className={cn("flex gap-3 p-4 rounded-xl border text-sm mt-2", d ? "border-zinc-700 bg-zinc-900 text-zinc-400" : "border-zinc-200 bg-zinc-50 text-zinc-600")}>
                    <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span>If you see a version number, the CLI is installed correctly and ready to use.</span>
                  </div>
                </Step>
              </div>

              {/* STEP 3 — Run */}
              <div id="run-thrust">
                <Step num="3" title="Initialize Thrust in your project" d={d}>
                  <p className="mb-4">Navigate to your project's root folder and run the init command. This starts the onboarding flow and connects the local watcher to your Thrust account.</p>
                  <CodeBlock code={`cd your-project-folder\nthrust`} d={d} />
                  <p className="mt-4 mb-4">
                    Once initialized, Thrust opens the local web console automatically in your browser at:
                  </p>
                  <CodeBlock code="http://localhost:8765" d={d} />
                  <p className="mt-4 mb-2">
                    From the console you can log in, connect your project, and start receiving Thrusts. The terminal stays running in the background — it's the only time you'll need it.
                  </p>
                  <div className={cn("flex gap-3 p-4 rounded-xl border text-sm mt-4", d ? "border-purple-500/20 bg-purple-500/5 text-purple-300" : "border-purple-200 bg-purple-50 text-purple-700")}>
                    <Zap size={16} className="text-purple-400 shrink-0 mt-0.5" />
                    <span>Keep the terminal running while you work. The local watcher only runs while this process is active. You can run it in a background tab or add it to your project's startup script.</span>
                  </div>
                </Step>
              </div>

              {/* STEP 4 — Next */}
              <div id="whats-next">
                <Step num="4" title="You're set up" done d={d}>
                  <p className="mb-6">Thrust is now watching your project. From here you can:</p>
                  <div className="space-y-3">
                    {[
                      { label: "Connect AI tools via MCP", desc: "Let Cursor, Claude Code, or n8n read your project context automatically.", action: () => setPage("mcp") },
                      { label: "Configure your morning briefing", desc: "Set your timezone in the console so your 5AM briefings land at the right time." },
                      { label: "Point Thrust at more projects", desc: "Run thrust init in any other project folder. Each gets its own isolated context." }
                    ].map((item, i) => (
                      <div key={i} className={cn("flex items-center gap-4 p-4 rounded-xl border transition-all", item.action ? "cursor-pointer hover:border-purple-400/40" : "", d ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200")} onClick={item.action}>
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", d ? "bg-zinc-800" : "bg-zinc-100")}>
                          <ArrowRight size={14} className="text-purple-400" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">{item.label}</p>
                          <p className={cn("text-xs mt-0.5", d ? "text-zinc-500" : "text-zinc-400")}>{item.desc}</p>
                        </div>
                        {item.action && <ChevronRight size={16} className="ml-auto text-zinc-500 shrink-0" />}
                      </div>
                    ))}
                  </div>
                </Step>
              </div>
            </div>
          )}

          {/* ─── MCP PAGE ─── */}
          {page === "mcp" && (
            <div>
              <div className="mb-12">
                <Badge color="blue">Integrations</Badge>
                <h1 className="text-4xl md:text-5xl font-black mt-4 mb-4 tracking-tight">Connecting External Tools</h1>
                <p className={cn("text-lg leading-relaxed", d ? "text-zinc-400" : "text-zinc-600")}>
                  Thrust exposes your active project's context — its requirements, current thrust, recent timeline, and milestone status — through a local MCP (Model Context Protocol) server. Any MCP-compatible AI tool can read this context and use it to give you project-aware responses.
                </p>
              </div>

              {/* HOW IT WORKS */}
              <div className={cn("p-6 rounded-2xl border mb-10", d ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200 shadow-sm")}>
                <h3 className="font-black text-base mb-3">How the MCP server works</h3>
                <p className={cn("text-sm leading-relaxed mb-4", d ? "text-zinc-400" : "text-zinc-600")}>
                  Running <code className={cn("px-1.5 py-0.5 rounded font-mono text-purple-400", d ? "bg-zinc-800" : "bg-zinc-100")}>thrust mcp</code> starts a local MCP server over standard I/O (stdio). Any connected AI client can then call tools and read resources from it, including:
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    ["get_project_context", "Full PRD and architecture doc"],
                    ["get_active_thrust", "Current tasks and briefing"],
                    ["get_timeline", "Recent project activity log"],
                    ["get_milestones", "Milestone status and completion"]
                  ].map(([name, desc]) => (
                    <div key={name} className={cn("p-3 rounded-xl border", d ? "bg-zinc-950 border-zinc-800" : "bg-zinc-50 border-zinc-200")}>
                      <code className="text-purple-400 text-xs block mb-1 font-mono">{name}</code>
                      <span className={cn("text-xs", d ? "text-zinc-500" : "text-zinc-500")}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={cn("flex gap-3 p-4 rounded-xl border text-sm mb-10", d ? "border-amber-500/20 bg-amber-500/5 text-amber-400" : "border-amber-200 bg-amber-50 text-amber-700")}>
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>The MCP server only runs when you explicitly start it with <code className="bg-black/20 px-1 rounded font-mono">thrust mcp</code>. It is separate from the file watcher. Most client configurations below handle starting it automatically.</span>
              </div>

              {/* MCP CLIENT CARDS */}
              <h2 className="text-2xl font-black mb-6">Supported Clients</h2>
              <div className="space-y-4">
                {MCP_CLIENTS.map(c => <MCPCard key={c.name} {...c} d={d} />)}
              </div>

              <div className={cn("mt-12 p-6 rounded-2xl border", d ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200")}>
                <h3 className="font-black text-base mb-2">Don't see your tool?</h3>
                <p className={cn("text-sm leading-relaxed mb-4", d ? "text-zinc-400" : "text-zinc-600")}>
                  Any tool that supports the Model Context Protocol over stdio can connect to Thrust. The command is always <code className={cn("px-1.5 py-0.5 rounded font-mono text-purple-400 text-xs", d ? "bg-zinc-800" : "bg-zinc-100")}>thrust mcp</code>. Check your tool's MCP documentation for the exact config format.
                </p>
                <a href="mailto:publicstring.care@gmail.com" className={cn("inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-lg border transition-colors", d ? "border-zinc-700 hover:bg-zinc-800 text-zinc-300" : "border-zinc-200 hover:bg-zinc-100 text-zinc-700")}>
                  <ExternalLink size={14} /> Request an integration guide
                </a>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
